import { DEFAULT_DATASET_CODE } from '../datasets';
import { MY_TEAM_DATASET_CODE } from '../datasets/myTeam';
import { DemoUrlState } from '../preview/demoParam';

// Module-level arming state for the "Try It Now" preview sandbox. Lives outside React because
// the axios adapter that runs the fail-closed mock is not a React component.
//
// Armed is a session-wide flag derived from the sessionStorage preview intent at bootstrap
// (primeArmedFromIntent). It is set only when a trial prospect enters via "Try It Now" (which
// writes the intent) and cleared on exit — so the mock can never intercept a paying user's real
// API calls.

interface PreviewState {
  armed: boolean;
  datasetCode: string;
  solutionCode: string | null;
}

const state: PreviewState = {
  armed: false,
  datasetCode: DEFAULT_DATASET_CODE,
  solutionCode: null,
};

// While armed, the demo must occupy exactly ONE history entry.
//
// `exitPreview` can only `replace` the entry the user is currently on, so anything the demo PUSHES
// outlives it. The Solution Finder closes with `navigate(-1)`, so those leftovers get walked back
// onto: the page loads un-armed, the real app renders on a demo URL, and it asks the real API for
// the sandbox's synthetic ids ("Employee not found in your team").
//
// Patching pushState is deliberate over tagging each navigation with `replace`. The demo is reached
// through the ordinary app — tabs, nav links, dialogs, drill-ins, all of which push — and an earlier
// attempt to tag them missed several; a new call site would miss again. react-router's history and
// the app's own `window.history.pushState` calls both funnel through here, so one swap covers every
// present and future navigation. Restored on disarm, and the exit reload discards it regardless.
// Known consequence: react-router still increments its own `idx` per push, so that counter runs
// ahead of the real entry count. Inert here because `idx` is only used to compute the POP `delta`,
// which <BrowserRouter> discards. Adopting a DATA router (createBrowserRouter/RouterProvider) or
// `useBlocker` would make it load-bearing — a blocked POP would try to traverse by a stale delta.
let patched = false;
let originalPushState: History['pushState'] | null = null;

function suppressHistoryPushes(): void {
  if (patched) return; // arming is idempotent — never re-capture the patched function
  patched = true;
  originalPushState = window.history.pushState;
  window.history.pushState = function (this: History, ...args: Parameters<History['pushState']>) {
    return window.history.replaceState(...args);
  };
}

// Restores by assignment, so it discards any wrapper layered on top since arming. Safe only because
// every disarm is followed by a hard reload; if that ever stops being true, unwind properly.
function restoreHistoryPushes(): void {
  if (!patched) return;
  patched = false;
  if (originalPushState) window.history.pushState = originalPushState;
  originalPushState = null;
}

export function armPreview(solutionCode: string, datasetCode: string = DEFAULT_DATASET_CODE): void {
  state.armed = true;
  state.solutionCode = solutionCode;
  state.datasetCode = datasetCode;
  suppressHistoryPushes();
}

export function disarmPreview(): void {
  state.armed = false;
  state.solutionCode = null;
  // Reset the dataset too, or `isMyTeamActive()` keeps answering "own" after the demo has ended.
  state.datasetCode = DEFAULT_DATASET_CODE;
  restoreHistoryPushes();
}

export function isPreviewArmed(): boolean {
  return state.armed;
}

export function getPreviewDatasetCode(): string {
  return state.datasetCode;
}

/**
 * True while the armed session is showing the TM's real team rather than the synthetic fixture.
 * Owned here, alongside the state it reads — `previewIdentity` re-exports it for view code.
 */
export function isMyTeamActive(): boolean {
  return state.datasetCode === MY_TEAM_DATASET_CODE;
}

/**
 * The params an armed session's URL should carry, or null outside a demo. Lives here because both
 * halves come from arming state, and callers should never derive one without the other.
 */
export function getPreviewUrlState(): DemoUrlState {
  if (!state.solutionCode) return null;
  return { solutionCode: state.solutionCode, view: isMyTeamActive() ? 'own' : 'sample' };
}
